import React, { useCallback, useMemo } from 'react';
import {
  AbsenceFilesPlaceholderIcon,
  Box,
  List,
  NoSearchItemsPlaceholder,
  PaginationBar,
  SearchInput,
  SearchPlaceholderIcon,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { ALL_OPTION, SORT_OPTIONS } from '@/common/constants';
import { useDownloadFileById, useOpenFile } from '@/modules/core';
import dropdownStateAllToggling, {
  checkIsAllOptionSelected,
} from '@/utils/handlers/dropdownStateAllToggling';

import { useSearchParams } from './hooks';
import { useStatementsQuery } from './queries';
import StatementsListItem from './StatementsListItem';

interface Props {
  accountId?: string;
  employeeId?: string;
}

const StatementsList: React.FC<Props> = ({ accountId, employeeId }) => {
  const {
    page,
    perPage,
    searchString,
    currentSearch,
    setSearchString,
    setPerPage,
    setPage,
    sortParams,
    setSortParams,
    years,
    setYears,
  } = useSearchParams();

  const yearsValues = useMemo(() => dropdownStateAllToggling(years), [years]);
  const isAllYearsSelected = useMemo(() => checkIsAllOptionSelected(years), [years]);

  const { data, total, isLoading } = useStatementsQuery({
    employeeId,
    page,
    pageSize: perPage,
    searchString: currentSearch,
    sortBy: sortParams,
    years: isAllYearsSelected ? [] : yearsValues,
  });

  const yearOptions = useMemo(() => {
    const startYear = 2022;
    const currentYear = dayjs().year();

    const statementsYears = Array.from(
      { length: currentYear - startYear },
      (_, index) => currentYear - index
    );

    const statementsYearsOptions = statementsYears.map((statementYear) => ({
      key: `${statementYear}`,
      value: `${statementYear}`,
      title: `${statementYear}`,
    }));

    return [ALL_OPTION, ...statementsYearsOptions];
  }, []);

  const areFiltersUsed = useMemo(
    () => currentSearch || !isAllYearsSelected,
    [currentSearch, isAllYearsSelected]
  );

  const isSearchingDisabled = useMemo(() => !(areFiltersUsed || total), [areFiltersUsed, total]);
  const { handleDownload } = useDownloadFileById();
  const { handleOpen } = useOpenFile();
  const handleDownloadFile = useCallback(
    (id: string) => {
      const foundItem = data?.find((item) => item.id == id);
      if (foundItem) {
        if (foundItem.url) {
          handleDownload(id, foundItem.url, foundItem.name || 'Statement');
        } else if (foundItem.documentId) {
          handleDownload(foundItem.documentId, undefined, foundItem.name || 'Statement');
        }
      }
    },
    [data, handleDownload]
  );
  const handleOpenFile = useCallback(
    (id: string) => {
      const foundItem = data?.find((item) => item.id == id);
      if (foundItem) {
        if (foundItem.url) {
          handleOpen(id, foundItem.url);
        } else if (foundItem.documentId) {
          handleOpen(foundItem.documentId);
        }
      }
    },
    [data, handleOpen]
  );
  return (
    <Box data-testId="statements-list" pad="l" direction="column" background="module" round="moduleRound">
      <Box margin={{ bottom: 'l' }} direction="row" align="center" justify="between">
        <Box>
          <Text weight="bold" color="textBody" size="large" style={{ whiteSpace: 'nowrap' }}>
            Files: {total}
          </Text>
        </Box>
        <Box direction="row" justify="end">
          <Box width="220px">
            <SearchInput
              testId="statements-list_search"
              value={searchString}
              onChange={setSearchString}
              disabled={isSearchingDisabled}
              placeholder="Search"
              hasSearchingHistory
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} width="180px">
            <SelectDropdown
              id="years"
              name="Years"
              prefix="Years"
              testId="statements-list_years"
              values={yearsValues}
              onChangeValues={setYears}
              options={yearOptions}
              disabled={isSearchingDisabled}
              activeTitle
              allMode
              ellipsisMode
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} width="180px">
            <SelectDropdown
              id="sort_by"
              name="Sort by"
              prefix="Sort by"
              testId="statements-list_sort"
              value={sortParams}
              onChange={setSortParams}
              options={SORT_OPTIONS}
              disabled={isSearchingDisabled}
              activeTitle
              singleMode
            />
          </Box>
        </Box>
      </Box>
      <List>
        <List.Header>
          <List.Header.Item flex={1}>Statement period</List.Header.Item>
          <List.Header.Item>Actions</List.Header.Item>
        </List.Header>
      </List>
      {isLoading ? (
        <List.Preloader testId="statements-list_loading" count={3} />
      ) : (
        <>
          {!data.length && (
            <NoSearchItemsPlaceholder
              data-testid="statements-list_placeholder"
              placeholderTitle={areFiltersUsed ? 'No results have been found' : 'There are no files yet'}
              node={
                areFiltersUsed ? (
                  <SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />
                ) : (
                  <AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />
                )
              }
              description={
                areFiltersUsed ? "Try to adjust your search or filter to find what you're looking for." : ''
              }
            />
          )}
          {data.map((item) => (
            <StatementsListItem
              onOpen={handleOpenFile}
              onDownload={handleDownloadFile}
              key={item.id}
              {...item}
            />
          ))}
        </>
      )}

      <PaginationBar
        testId="statements-list_paginator"
        page={page}
        total={total}
        pageSize={perPage}
        onChangePage={setPage}
        onChangePageSize={setPerPage}
      />
    </Box>
  );
};

export default StatementsList;
